import { useState, useEffect, useMemo } from 'react';
import { X, Trash2, Pencil, Check, Package, Calendar, List, Download, Plus } from 'lucide-react';
import { SheetShell } from '../../components/boothly/sheet-shell.jsx';
import { Z } from '../../lib/z-index.js';
import { useToast } from '../../components/boothly/toast-provider.jsx';
import { useConfirm } from '../../components/boothly/confirm-provider.jsx';
import { SectionLabel, SettingsGroup, SettingsRow, SettingsValueRow, SettingsDivider } from '../../components/boothly/settings-ui.jsx';

// ── FleaCategoriesPanel ───────────────────────────────────────────────────────

function FleaCategoriesPanel({ store, authToken, dark, categories, onCategoriesChanged, onClose }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [addingName, setAddingName] = useState('');
  const [addingBusy, setAddingBusy] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const sortedCats = useMemo(() => {
    return [...(categories || [])].sort((a, b) => {
      if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
      if (a.is_default && b.is_default) return (a.sort_order || 0) - (b.sort_order || 0);
      return (a.name || '').localeCompare(b.name || '', 'de');
    });
  }, [categories]);

  const startEdit = (c) => { setEditingId(c.id); setEditingName(c.name); };

  const saveEdit = async (cat) => {
    const newName = editingName.trim();
    if (!newName || newName === cat.name) { setEditingId(null); return; }
    setBusyId(cat.id);
    try {
      const res = await fetch(`/api/stores/${store.id}/flea-categories/${cat.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ name: newName }),
      });
      if (!res.ok) throw new Error(res.status === 409 ? 'Name existiert bereits' : 'Speichern fehlgeschlagen');
      const updated = await res.json();
      onCategoriesChanged((categories || []).map(c => c.id === cat.id ? updated : c));
      toast.success('Kategorie umbenannt');
    } catch (e) {
      toast.error(e.message || 'Speichern fehlgeschlagen');
    } finally {
      setBusyId(null);
      setEditingId(null);
    }
  };

  const deleteCat = async (cat) => {
    setBusyId(cat.id);
    try {
      const res = await fetch(`/api/stores/${store.id}/flea-categories/${cat.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('Löschen fehlgeschlagen');
      onCategoriesChanged((categories || []).filter(c => c.id !== cat.id));
      toast.success('Kategorie gelöscht');
    } catch (e) {
      toast.error(e.message || 'Löschen fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  const addCategory = async () => {
    const name = addingName.trim();
    if (!name) return;
    setAddingBusy(true);
    try {
      const res = await fetch(`/api/stores/${store.id}/flea-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error(res.status === 409 ? 'Name existiert bereits' : 'Hinzufügen fehlgeschlagen');
      const newCat = await res.json();
      onCategoriesChanged([...(categories || []), newCat]);
      toast.success('Kategorie hinzugefügt');
      setAddingName('');
      setShowAddForm(false);
    } catch (e) {
      toast.error(e.message || 'Hinzufügen fehlgeschlagen');
    } finally {
      setAddingBusy(false);
    }
  };

  return (
    <SheetShell
      open
      onClose={onClose}
      grouped
      zIndex={Z.nestedBackdrop}
      testId="flea-categories-sheet"
      title="Kategorien"
      subtitle={`${(categories || []).length} Kategorien`}
    >
      <div className="pb-6 pt-1">
        <div className="mb-4 overflow-hidden rounded-[16px] bg-[var(--bg-L1)] dark:bg-[var(--bg-L2)]">
          {sortedCats.map((c, idx) => {
            const isEditing = editingId === c.id;
            const busy = busyId === c.id;
            return (
              <div key={c.id} className="flex min-h-[52px] items-center gap-3 pl-4 pr-1" style={{ opacity: busy ? 0.6 : 1 }}>
                <div className={`flex min-h-[52px] min-w-0 flex-1 items-center gap-2 ${idx ? 'border-t border-[var(--separator)]' : ''}`}>
                  {isEditing ? (
                    <input
                      autoFocus
                      type="text"
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(c); if (e.key === 'Escape') setEditingId(null); }}
                      onBlur={() => saveEdit(c)}
                      className="min-w-0 flex-1 bg-transparent text-[17px] outline-none"
                      style={{ boxShadow: 'inset 0 -2px 0 var(--accent-troedel)' }}
                    />
                  ) : (
                    <span className="min-w-0 flex-1 truncate text-[17px]">{c.name}</span>
                  )}
                  <span className="shrink-0 text-[13px] text-[var(--label-secondary)]">
                    {c.is_default ? 'Standard' : 'Eigene'}
                  </span>
                  {!isEditing && (
                    <div className="flex shrink-0 items-center">
                      <button onClick={() => startEdit(c)} disabled={busy} aria-label={`${c.name} umbenennen`} className="flex h-11 w-11 items-center justify-center rounded-full text-[var(--accent-troedel)] active:bg-[var(--fill-quaternary)]">
                        <Pencil size={17} />
                      </button>
                      {!c.is_default && (
                        <button onClick={async () => {
                          const ok = await confirm({ title: 'Kategorie löschen?', subtitle: `„${c.name}" wird gelöscht. Items werden auf „Sonstiges" umgestellt.`, confirmLabel: 'Löschen', destructive: true });
                          if (ok) deleteCat(c);
                        }} disabled={busy} aria-label={`${c.name} löschen`} className="flex h-11 w-11 items-center justify-center rounded-full text-[var(--accent-red)] active:bg-[var(--fill-quaternary)]">
                          <Trash2 size={17} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {showAddForm ? (
          <div className="flex h-[52px] items-center gap-1 rounded-[16px] bg-[var(--bg-L1)] pl-4 pr-1 dark:bg-[var(--bg-L2)]">
            <input
              autoFocus
              type="text"
              placeholder="Name der Kategorie"
              value={addingName}
              onChange={e => setAddingName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addCategory(); if (e.key === 'Escape') { setShowAddForm(false); setAddingName(''); } }}
              className="min-w-0 flex-1 bg-transparent text-[17px] outline-none placeholder:text-[var(--label-tertiary)]"
            />
            <button
              onClick={addCategory}
              disabled={addingBusy || !addingName.trim()}
              aria-label="Kategorie anlegen"
              className="flex h-11 w-11 items-center justify-center rounded-full text-[var(--accent-troedel)] disabled:opacity-40"
            >
              <Check size={20} strokeWidth={2.6} />
            </button>
            <button onClick={() => { setShowAddForm(false); setAddingName(''); }} aria-label="Abbrechen" className="flex h-11 w-11 items-center justify-center rounded-full text-[var(--label-secondary)]">
              <X size={18} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex h-[52px] w-full items-center gap-2 rounded-[16px] bg-[var(--bg-L1)] px-4 text-[17px] text-[var(--accent-troedel)] active:bg-[var(--fill-quaternary)] dark:bg-[var(--bg-L2)]"
          >
            <Plus size={20} />
            Neue Kategorie
          </button>
        )}
      </div>
    </SheetShell>
  );
}

// ── FleaSettings ──────────────────────────────────────────────────────────────

export function FleaSettings({
  open, onOpenChange, store, authToken, dark, onStoreDeleted,
  categories, onCategoriesChanged, itemsCount, sessionsCount,
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [categoriesPanelOpen, setCategoriesPanelOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await fetch(`/api/stores/${store.id}/export`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error('Export fehlgeschlagen');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${store.name || 'troedel'}-export.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export heruntergeladen');
    } catch (e) {
      toast.error(e.message || 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const handleDestroyStore = async () => {
    try {
      const res = await fetch(`/api/stores/${store.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.reason || 'Löschen fehlgeschlagen');
        return;
      }
      toast.success('Trödel-Bereich gelöscht');
      onStoreDeleted?.();
    } catch {
      toast.error('Netzwerkfehler');
    }
  };

  if (!open) return null;

  return (
    <>
      <SheetShell
        open={open}
        onClose={() => onOpenChange(false)}
        grouped
        testId="flea-settings-sheet"
        title="Einstellungen"
        subtitle={store?.name}
      >
        <div className="pb-6">
          <SectionLabel>Inventar</SectionLabel>
          <SettingsGroup>
            <SettingsValueRow icon={<Package size={18} />} label="Items" value={String(itemsCount ?? 0)} />
            <SettingsDivider />
            <SettingsValueRow icon={<Calendar size={18} />} label="Markt-Tage" value={String(sessionsCount ?? 0)} />
            <SettingsDivider />
            <SettingsValueRow
              icon={<List size={18} />}
              label="Kategorien verwalten"
              value={String((categories || []).length)}
              onClick={() => setCategoriesPanelOpen(true)}
            />
          </SettingsGroup>

          <SectionLabel>Daten</SectionLabel>
          <SettingsGroup>
            <SettingsRow
              icon={<Download size={18} />}
              label={exporting ? 'Exportiere…' : 'Trödel-Daten exportieren'}
              sub="ZIP mit Items, Sessions und Verkäufen"
              onClick={!exporting ? handleExport : undefined}
            />
          </SettingsGroup>

          <SectionLabel>Bereich</SectionLabel>
          <SettingsGroup>
            <SettingsRow
              icon={<Trash2 size={18} />}
              label="Trödel-Bereich löschen"
              sub="Alle Daten werden permanent gelöscht"
              destructive
              onClick={async () => {
                const ok = await confirm({
                  title: 'Trödel-Bereich löschen?',
                  subtitle: 'Alle Items, Markt-Tage und Verkäufe werden permanent gelöscht.',
                  warning: 'Diese Aktion kann nicht rückgängig gemacht werden.',
                  confirmLabel: 'Löschen',
                  destructive: true,
                });
                if (ok) handleDestroyStore();
              }}
            />
          </SettingsGroup>
        </div>
      </SheetShell>

      {categoriesPanelOpen && (
        <FleaCategoriesPanel
          store={store}
          authToken={authToken}
          dark={dark}
          categories={categories || []}
          onCategoriesChanged={onCategoriesChanged}
          onClose={() => setCategoriesPanelOpen(false)}
        />
      )}
    </>
  );
}
